import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import Input from "../../../components/Input";
import { InputText } from "../../../components/InputText";
import { Label } from "../../../components/ui/label";
import { TextArea } from "../../../components/TextArea";
import { SelectSingle } from "../../../components/SelectSingle";
import { MilitarSelectField } from "../../../components/MilitarSelectField";
import { api } from "../../../services/api";
import { usePermissions, PERMISSOES } from "../../../hooks/usePermissions";
import {
  Award,
  Search,
  ArrowUp,
  ArrowDown,
  Plus,
  Edit,
  Trash2,
  ShoppingBag,
  FolderOpen,
} from "lucide-react";

interface Militar {
  id: string;
  nick: string;
  patente: {
    id: string;
    patente: string;
  };
}

interface MilitarEmblema {
  id: string;
  militar: Militar;
  militarAtribuiu: Militar | null;
  criado_em: string;
}

interface EmblemaComMilitares {
  id: string;
  emblema: string;
  img: string;
  descricao: string | null;
  ordem: number;
  valor: number;
  status: string;
  militares: MilitarEmblema[];
}

interface CategoriaEmblema {
  id: string;
  nome: string;
  ordem: number;
}

interface Emblema {
  id: string;
  emblema: string;
  img: string;
  descricao?: string;
  ordem: number;
  valor: number;
  status: string;
  categoria?: CategoriaEmblema | null;
}

export function GerenciarEmblemas() {
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState<"emblemas" | "tipos" | "categorias">("emblemas");
  const [loading, setLoading] = useState(false);

  // Tab Emblemas (com militares)
  const [emblemasComMilitares, setEmblemasComMilitares] = useState<EmblemaComMilitares[]>([]);
  const [searchEmblema, setSearchEmblema] = useState("");
  const [searchNick, setSearchNick] = useState("");

  // Tab Tipos de Emblemas (CRUD)
  const [emblemas, setEmblemas] = useState<Emblema[]>([]);
  const [filteredEmblemas, setFilteredEmblemas] = useState<Emblema[]>([]);
  const [searchTipo, setSearchTipo] = useState("");
  const [isModalEmblemaOpen, setIsModalEmblemaOpen] = useState(false);
  const [editingEmblema, setEditingEmblema] = useState<Emblema | null>(null);
  const [formNomeEmblema, setFormNomeEmblema] = useState("");
  const [formImgEmblema, setFormImgEmblema] = useState("");
  const [formDescricaoEmblema, setFormDescricaoEmblema] = useState("");
  const [formValorEmblema, setFormValorEmblema] = useState("");
  const [formStatusEmblema, setFormStatusEmblema] = useState("A");
  const [formCategoriaIdEmblema, setFormCategoriaIdEmblema] = useState("");

  // Tab Categorias de Emblemas
  const [categorias, setCategorias] = useState<CategoriaEmblema[]>([]);
  const [isModalCategoriaOpen, setIsModalCategoriaOpen] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState<CategoriaEmblema | null>(null);
  const [formNomeCategoria, setFormNomeCategoria] = useState("");
  const [isExcluirCategoriaModalOpen, setIsExcluirCategoriaModalOpen] = useState(false);
  const [categoriaToDelete, setCategoriaToDelete] = useState<{ id: string; nome: string } | null>(null);

  // Modal Atribuir Emblema
  const [isAtribuirModalOpen, setIsAtribuirModalOpen] = useState(false);
  const [selectedMilitarId, setSelectedMilitarId] = useState("");
  const [selectedMilitarNick, setSelectedMilitarNick] = useState("");
  const [selectedEmblemaId, setSelectedEmblemaId] = useState("");

  // Modal Remover Emblema do Militar
  const [isRemoverModalOpen, setIsRemoverModalOpen] = useState(false);
  const [militarEmblemaToRemove, setMilitarEmblemaToRemove] = useState<{
    id: string;
    militarNick: string;
    emblemaNome: string;
  } | null>(null);

  // Modal Excluir Emblema
  const [isExcluirEmblemaModalOpen, setIsExcluirEmblemaModalOpen] = useState(false);
  const [emblemaToDelete, setEmblemaToDelete] = useState<{
    id: string;
    emblema: string;
  } | null>(null);

  useEffect(() => {
    if (activeTab === "emblemas") {
      loadEmblemasComMilitares();
      // Carregar lista de emblemas para modal
      if (emblemas.length === 0) {
        loadEmblemas();
      }
    } else if (activeTab === "tipos") {
      loadEmblemas();
      if (categorias.length === 0) {
        loadCategorias();
      }
    } else if (activeTab === "categorias") {
      loadCategorias();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!searchTipo.trim()) {
      setFilteredEmblemas(emblemas);
    } else {
      const filtered = emblemas.filter((e) =>
        e.emblema.toLowerCase().includes(searchTipo.toLowerCase())
      );
      setFilteredEmblemas(filtered);
    }
  }, [searchTipo, emblemas]);

  const loadEmblemasComMilitares = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchEmblema) params.append("emblema", searchEmblema);
      if (searchNick) params.append("nick", searchNick);

      const response = await api.get(`/emblemas/militares?${params.toString()}`);
      setEmblemasComMilitares(response.data);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar emblemas", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const loadEmblemas = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await api.get("/emblemas");
      setEmblemas(response.data);
      setFilteredEmblemas(response.data);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar emblemas", { variant: "error" });
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const loadCategorias = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await api.get("/categorias-emblemas");
      setCategorias(response.data);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar categorias", { variant: "error" });
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleSearchEmblemas = (e: React.FormEvent) => {
    e.preventDefault();
    loadEmblemasComMilitares();
  };

  const moveEmblema = async (index: number, direction: "up" | "down") => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === emblemas.length - 1)
    ) {
      return;
    }

    const newIndex = direction === "up" ? index - 1 : index + 1;
    const newEmblemas = [...emblemas];
    const [removed] = newEmblemas.splice(index, 1);
    newEmblemas.splice(newIndex, 0, removed);

    const emblemasAtualizados = newEmblemas.map((e, idx) => ({
      ...e,
      ordem: idx + 1,
    }));

    setEmblemas(emblemasAtualizados);
    setFilteredEmblemas(emblemasAtualizados);

    try {
      const ordens = emblemasAtualizados.map((e) => ({
        id: e.id,
        ordem: e.ordem,
      }));

      await api.patch("/emblemas/reordenar", { emblemas: ordens });
      enqueueSnackbar("Ordem atualizada com sucesso", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Erro ao atualizar ordem", { variant: "error" });
      await loadEmblemas(false);
    }
  };

  // Emblema CRUD
  const openCreateEmblemaModal = () => {
    setEditingEmblema(null);
    setFormNomeEmblema("");
    setFormImgEmblema("");
    setFormDescricaoEmblema("");
    setFormValorEmblema("0");
    setFormStatusEmblema("A");
    setFormCategoriaIdEmblema("");
    setIsModalEmblemaOpen(true);
  };

  const openEditEmblemaModal = (emblema: Emblema) => {
    setEditingEmblema(emblema);
    setFormNomeEmblema(emblema.emblema);
    setFormImgEmblema(emblema.img);
    setFormDescricaoEmblema(emblema.descricao || "");
    setFormValorEmblema(emblema.valor.toString());
    setFormStatusEmblema(emblema.status);
    setFormCategoriaIdEmblema(emblema.categoria?.id || "");
    setIsModalEmblemaOpen(true);
  };

  const closeEmblemaModal = () => {
    setIsModalEmblemaOpen(false);
    setEditingEmblema(null);
    setFormNomeEmblema("");
    setFormImgEmblema("");
    setFormDescricaoEmblema("");
    setFormValorEmblema("0");
    setFormStatusEmblema("A");
    setFormCategoriaIdEmblema("");
  };

  const handleSaveEmblema = async () => {
    if (!formNomeEmblema || !formImgEmblema || !formValorEmblema) {
      enqueueSnackbar("Preencha todos os campos obrigatórios", { variant: "warning" });
      return;
    }

    const valor = parseInt(formValorEmblema);
    if (isNaN(valor) || valor < 0) {
      enqueueSnackbar("Valor deve ser um número maior ou igual a 0", { variant: "warning" });
      return;
    }

    try {
      setLoading(true);

      if (editingEmblema) {
        await api.put(`/emblemas/${editingEmblema.id}`, {
          emblema: formNomeEmblema,
          img: formImgEmblema,
          descricao: formDescricaoEmblema,
          valor,
          status: formStatusEmblema,
          id_categoria: formCategoriaIdEmblema || null,
        });
        enqueueSnackbar("Emblema atualizado com sucesso", { variant: "success" });
      } else {
        await api.post("/emblemas", {
          emblema: formNomeEmblema,
          img: formImgEmblema,
          descricao: formDescricaoEmblema,
          valor,
          status: formStatusEmblema,
          id_categoria: formCategoriaIdEmblema || undefined,
        });
        enqueueSnackbar("Emblema criado com sucesso", { variant: "success" });
      }

      closeEmblemaModal();
      loadEmblemas();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao salvar emblema",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const openExcluirEmblemaModal = (id: string, nome: string) => {
    setEmblemaToDelete({ id, emblema: nome });
    setIsExcluirEmblemaModalOpen(true);
  };

  const closeExcluirEmblemaModal = () => {
    setIsExcluirEmblemaModalOpen(false);
    setEmblemaToDelete(null);
  };

  const handleDeleteEmblema = async () => {
    if (!emblemaToDelete) return;

    try {
      await api.delete(`/emblemas/${emblemaToDelete.id}`);
      enqueueSnackbar("Emblema excluído com sucesso", { variant: "success" });
      closeExcluirEmblemaModal();
      loadEmblemas();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao excluir emblema",
        { variant: "error" }
      );
    }
  };

  // Atribuir Emblema
  const openAtribuirModal = () => {
    setSelectedMilitarId("");
    setSelectedMilitarNick("");
    setSelectedEmblemaId("");
    setIsAtribuirModalOpen(true);
  };

  const closeAtribuirModal = () => {
    setIsAtribuirModalOpen(false);
    setSelectedMilitarId("");
    setSelectedMilitarNick("");
    setSelectedEmblemaId("");
  };

  const handleAtribuirEmblema = async () => {
    if (!selectedMilitarId || !selectedEmblemaId) {
      enqueueSnackbar("Preencha todos os campos", { variant: "warning" });
      return;
    }

    try {
      setLoading(true);
      await api.post("/emblemas/atribuir", {
        idMilitar: selectedMilitarId,
        idEmblema: selectedEmblemaId,
      });
      enqueueSnackbar("Emblema atribuído com sucesso", { variant: "success" });
      closeAtribuirModal();
      loadEmblemasComMilitares();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao atribuir emblema",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  // Categorias de Emblemas CRUD
  const openCreateCategoriaModal = () => {
    setEditingCategoria(null);
    setFormNomeCategoria("");
    setIsModalCategoriaOpen(true);
  };

  const openEditCategoriaModal = (categoria: CategoriaEmblema) => {
    setEditingCategoria(categoria);
    setFormNomeCategoria(categoria.nome);
    setIsModalCategoriaOpen(true);
  };

  const closeCategoriaModal = () => {
    setIsModalCategoriaOpen(false);
    setEditingCategoria(null);
    setFormNomeCategoria("");
  };

  const handleSaveCategoria = async () => {
    if (!formNomeCategoria.trim()) {
      enqueueSnackbar("Informe o nome da categoria", { variant: "warning" });
      return;
    }

    try {
      setLoading(true);
      if (editingCategoria) {
        await api.put(`/categorias-emblemas/${editingCategoria.id}`, { nome: formNomeCategoria });
        enqueueSnackbar("Categoria atualizada com sucesso", { variant: "success" });
      } else {
        await api.post("/categorias-emblemas", { nome: formNomeCategoria });
        enqueueSnackbar("Categoria criada com sucesso", { variant: "success" });
      }
      closeCategoriaModal();
      loadCategorias(false);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao salvar categoria", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const openExcluirCategoriaModal = (id: string, nome: string) => {
    setCategoriaToDelete({ id, nome });
    setIsExcluirCategoriaModalOpen(true);
  };

  const closeExcluirCategoriaModal = () => {
    setIsExcluirCategoriaModalOpen(false);
    setCategoriaToDelete(null);
  };

  const handleDeleteCategoria = async () => {
    if (!categoriaToDelete) return;
    try {
      await api.delete(`/categorias-emblemas/${categoriaToDelete.id}`);
      enqueueSnackbar("Categoria excluída com sucesso", { variant: "success" });
      closeExcluirCategoriaModal();
      loadCategorias(false);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao excluir categoria", { variant: "error" });
    }
  };

  const moveCategoria = async (index: number, direction: "up" | "down") => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === categorias.length - 1)
    ) {
      return;
    }

    const newIndex = direction === "up" ? index - 1 : index + 1;
    const newCategorias = [...categorias];
    const [removed] = newCategorias.splice(index, 1);
    newCategorias.splice(newIndex, 0, removed);

    const categoriasAtualizadas = newCategorias.map((c, idx) => ({ ...c, ordem: idx + 1 }));
    setCategorias(categoriasAtualizadas);

    try {
      const ordens = categoriasAtualizadas.map((c) => ({ id: c.id, ordem: c.ordem }));
      await api.patch("/categorias-emblemas/reordenar", { categoriaOrdens: ordens });
      enqueueSnackbar("Ordem atualizada com sucesso", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Erro ao atualizar ordem", { variant: "error" });
      await loadCategorias(false);
    }
  };

  // Remover Emblema do Militar
  const openRemoverModal = (
    militarEmblemaId: string,
    militarNick: string,
    emblemaNome: string
  ) => {
    setMilitarEmblemaToRemove({
      id: militarEmblemaId,
      militarNick,
      emblemaNome,
    });
    setIsRemoverModalOpen(true);
  };

  const closeRemoverModal = () => {
    setIsRemoverModalOpen(false);
    setMilitarEmblemaToRemove(null);
  };

  const handleRemoverEmblema = async () => {
    if (!militarEmblemaToRemove) return;

    try {
      setLoading(true);
      await api.delete(`/emblemas/militar/${militarEmblemaToRemove.id}`);
      enqueueSnackbar("Emblema removido com sucesso", { variant: "success" });
      closeRemoverModal();
      loadEmblemasComMilitares();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao remover emblema",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Award className="h-6 w-6 text-[oklch(0.45_0.15_142)]" />
          Emblemas
        </h1>
        <p className="text-muted-foreground mt-2">
          Gerencie os emblemas e suas atribuições
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "emblemas"
              ? "border-b-2 border-[oklch(0.45_0.15_142)] text-[oklch(0.45_0.15_142)]"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("emblemas")}
        >
          Emblemas
        </button>
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "tipos"
              ? "border-b-2 border-[oklch(0.45_0.15_142)] text-[oklch(0.45_0.15_142)]"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("tipos")}
        >
          Tipos de Emblemas
        </button>
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "categorias"
              ? "border-b-2 border-[oklch(0.45_0.15_142)] text-[oklch(0.45_0.15_142)]"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("categorias")}
        >
          Categorias
        </button>
      </div>

      {/* Tab: Emblemas */}
      {activeTab === "emblemas" && (
        <div className="space-y-6">
          {/* Filtros */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                    <Search className="w-5 h-5" />
                    Buscar Emblemas e Militares
                  </CardTitle>
                  <CardDescription>
                    Filtrar por emblema ou nick do militar
                  </CardDescription>
                </div>
                {hasPermission(PERMISSOES.ATRIBUIR_EMBLEMA) && (
                  <Button
                    onClick={openAtribuirModal}
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Atribuir Emblema
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearchEmblemas} className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="search-emblema">Emblema</Label>
                  <InputText
                    id="search-emblema"
                    name="search-emblema"
                    title=""
                    placeholder="Digite o nome do emblema..."
                    value={searchEmblema}
                    onChange={(e) => setSearchEmblema(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="search-nick-emblema">Nick do Militar</Label>
                  <InputText
                    id="search-nick-emblema"
                    name="search-nick-emblema"
                    title=""
                    placeholder="Digite o nick do militar..."
                    value={searchNick}
                    onChange={(e) => setSearchNick(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                  >
                    {loading ? "Buscando..." : "Buscar"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Resultados */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)]">Resultados</CardTitle>
              <CardDescription>
                {emblemasComMilitares.length} emblema(s) encontrado(s)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">Carregando...</div>
                </div>
              ) : emblemasComMilitares.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum emblema encontrado
                </div>
              ) : (
                <div className="space-y-6">
                  {emblemasComMilitares.map((emblema) => (
                    <div key={emblema.id} className="space-y-3">
                      <div className="flex items-center gap-3 pb-2 border-b-2 border-[oklch(0.45_0.15_142)]">
                        <img
                          src={emblema.img}
                          alt={emblema.emblema}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-[oklch(0.45_0.15_142)]">
                            {emblema.emblema}
                          </h3>
                          {emblema.descricao && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {emblema.descricao}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={emblema.status === "A" ? "default" : "secondary"}
                            className={emblema.status === "A" ? "bg-green-600 text-white" : "bg-gray-400 text-white"}
                          >
                            <ShoppingBag className="w-3 h-3 mr-1" />
                            {emblema.valor} BRCoins
                          </Badge>
                          <Badge variant="outline" className="ml-2">
                            {emblema.militares.length}{" "}
                            {emblema.militares.length === 1 ? "militar" : "militares"}
                          </Badge>
                        </div>
                      </div>

                      {emblema.militares.length === 0 ? (
                        <div className="text-sm text-muted-foreground ml-11">
                          Nenhum militar com este emblema
                        </div>
                      ) : (
                        <div className="ml-4 space-y-2">
                          {emblema.militares.map((me) => (
                            <div
                              key={me.id}
                              className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                            >
                              <img
                                src={`http://www.habbo.com.br/habbo-imaging/avatarimage?&user=${me.militar.nick}&action=std&direction=5&head_direction=2&img_format=png&gesture=std&headonly=1&size=l`}
                                alt={me.militar.nick}
                                className="w-12 h-12 rounded-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                              <div className="flex-1">
                                <div className="font-medium">
                                  {me.militar.nick}
                                </div>
                                {me.militarAtribuiu && (
                                  <div className="text-xs text-muted-foreground">
                                    Por: {me.militarAtribuiu.nick}
                                  </div>
                                )}
                              </div>
                              {hasPermission(PERMISSOES.REMOVER_EMBLEMA) && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    openRemoverModal(
                                      me.id,
                                      me.militar.nick,
                                      emblema.emblema
                                    )
                                  }
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab: Tipos de Emblemas */}
      {activeTab === "tipos" && (
        <div className="space-y-6">
          {/* Busca e Ações */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                    <Search className="w-5 h-5" />
                    Gerenciar Emblemas
                  </CardTitle>
                  <CardDescription>
                    Buscar e gerenciar emblemas da loja
                  </CardDescription>
                </div>
                {hasPermission(PERMISSOES.CRIAR_EMBLEMA) && (
                  <Button
                    onClick={openCreateEmblemaModal}
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Novo Emblema
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <InputText
                id="search-tipos"
                name="search-tipos"
                title=""
                placeholder="Buscar por emblema..."
                value={searchTipo}
                onChange={(e) => setSearchTipo(e.target.value)}
              />
            </CardContent>
          </Card>

          {/* Lista de Emblemas */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)]">Emblemas</CardTitle>
              <CardDescription>
                {emblemas.length} emblema(s) cadastrado(s)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">Carregando...</div>
                </div>
              ) : filteredEmblemas.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum emblema cadastrado
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredEmblemas.map((emblema, index) => (
                    <div
                      key={emblema.id}
                      className="flex items-center gap-3 p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex flex-col gap-1 mr-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveEmblema(index, "up")}
                          disabled={index === 0}
                          className="h-6 w-6 p-0"
                        >
                          <ArrowUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveEmblema(index, "down")}
                          disabled={index === emblemas.length - 1}
                          className="h-6 w-6 p-0"
                        >
                          <ArrowDown className="h-3 w-3" />
                        </Button>
                      </div>

                      <div className="flex items-center gap-4 flex-1">
                        <img
                          src={emblema.img}
                          alt={emblema.emblema}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <h3 className="font-semibold text-foreground">
                              {emblema.emblema}
                            </h3>
                            {emblema.descricao && (
                              <p className="text-sm text-muted-foreground">
                              {emblema.descricao}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <Badge
                              variant={emblema.status === "A" ? "default" : "secondary"}
                              className={`text-xs ${
                                emblema.status === "A"
                                  ? "bg-green-600 text-white"
                                  : "bg-gray-400 text-white"
                              }`}
                            >
                              {emblema.status === "A" ? "Ativo" : "Inativo"}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              <ShoppingBag className="w-3 h-3 mr-1" />
                              {emblema.valor} BRCoins
                            </Badge>
                            {emblema.categoria && (
                              <Badge variant="outline" className="text-xs text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/40">
                                <FolderOpen className="w-3 h-3 mr-1" />
                                {emblema.categoria.nome}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {hasPermission(PERMISSOES.EDITAR_EMBLEMA) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditEmblemaModal(emblema)}
                            className="text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 hover:bg-[oklch(0.45_0.15_142)]/10"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                        )}
                        {hasPermission(PERMISSOES.EXCLUIR_EMBLEMA) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openExcluirEmblemaModal(emblema.id, emblema.emblema)}
                            className="text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Excluir
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal: Criar/Editar Emblema */}
      {isModalEmblemaOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>
                {editingEmblema ? "Editar Emblema" : "Novo Emblema"}
              </CardTitle>
              <CardDescription>
                {editingEmblema
                  ? "Atualize os dados do emblema"
                  : "Crie um novo emblema para a loja"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                title="Nome do Emblema"
                value={formNomeEmblema}
                onChange={(e) => setFormNomeEmblema(e.target.value)}
                placeholder="Ex: Emblema Super"
              />
              <Input
                title="URL da Imagem"
                value={formImgEmblema}
                onChange={(e) => setFormImgEmblema(e.target.value)}
                placeholder="https://..."
              />
              <TextArea
                title="Descrição"
                value={formDescricaoEmblema}
                onChange={(e) => setFormDescricaoEmblema(e.target.value)}
                placeholder="Descrição do emblema (opcional)"
                rows={3}
              />
              <Input
                title="Valor (BRCoins)"
                type="number"
                value={formValorEmblema}
                onChange={(e) => setFormValorEmblema(e.target.value)}
                placeholder="0"
                min="0"
              />
              <SelectSingle
                title="Status  na Loja"
                value={formStatusEmblema}
                onChange={(e) => setFormStatusEmblema(e.target.value)}
                required
              >
                <option value="A">Ativo (visível na loja)</option>
                <option value="I">Inativo (oculto da loja)</option>
              </SelectSingle>
              <SelectSingle
                title="Categoria"
                value={formCategoriaIdEmblema}
                onChange={(e) => setFormCategoriaIdEmblema(e.target.value)}
              >
                <option value="">Sem categoria</option>
                {categorias.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </SelectSingle>
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveEmblema}
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  {editingEmblema ? "Atualizar" : "Criar"}
                </Button>
                <Button onClick={closeEmblemaModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal: Atribuir Emblema */}
      {isAtribuirModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                <Award className="w-5 h-5" />
                Atribuir Emblema a Militar
              </CardTitle>
              <CardDescription>
                Selecione o militar e o emblema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <MilitarSelectField
                label="Militar"
                value={selectedMilitarId}
                onChange={(id, nick) => {
                  setSelectedMilitarId(id);
                  setSelectedMilitarNick(nick);
                }}
                placeholder="Digite o nome do militar..."
                required
              />

              <SelectSingle
                title="Emblema"
                value={selectedEmblemaId}
                onChange={(e) => setSelectedEmblemaId(e.target.value)}
                required
              >
                <option value="">Selecione o emblema</option>
                {emblemas.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.emblema} ({e.valor} BRCoins)
                  </option>
                ))}
              </SelectSingle>

              <div className="flex gap-2">
                <Button
                  onClick={handleAtribuirEmblema}
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  {loading ? "Atribuindo..." : "Atribuir"}
                </Button>
                <Button onClick={closeAtribuirModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal: Remover Emblema */}
      {isRemoverModalOpen && militarEmblemaToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Remover Emblema
              </CardTitle>
              <CardDescription>
                Esta ação não pode ser desfeita
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-950/30 dark:border-red-800">
                <p className="text-sm text-foreground">
                  Tem certeza que deseja remover o emblema{" "}
                  <span className="font-semibold text-green-700 dark:text-green-400">
                    {militarEmblemaToRemove.emblemaNome}
                  </span>{" "}
                  do militar{" "}
                  <span className="font-semibold">
                    {militarEmblemaToRemove.militarNick}
                  </span>
                  ?
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleRemoverEmblema}
                  disabled={loading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? "Removendo..." : "Sim, Remover"}
                </Button>
                <Button onClick={closeRemoverModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal: Excluir Emblema */}
      {isExcluirEmblemaModalOpen && emblemaToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Excluir Emblema
              </CardTitle>
              <CardDescription>
                Esta ação não pode ser desfeita
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-950/30 dark:border-red-800">
                <p className="text-sm text-foreground">
                  Tem certeza que deseja excluir permanentemente o emblema{" "}
                  <span className="font-semibold text-green-700 dark:text-green-400">
                    {emblemaToDelete.emblema}
                  </span>
                  ?
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Todos os militares que possuem este emblema terão ele removido.
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleDeleteEmblema}
                  disabled={loading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? "Excluindo..." : "Sim, Excluir"}
                </Button>
                <Button onClick={closeExcluirEmblemaModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab: Categorias */}
      {activeTab === "categorias" && (
        <div className="space-y-6">
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                    <FolderOpen className="w-5 h-5" />
                    Categorias de Emblemas
                  </CardTitle>
                  <CardDescription>
                    Organize os emblemas em categorias para exibição na loja
                  </CardDescription>
                </div>
                {hasPermission(PERMISSOES.CRIAR_EMBLEMA) && (
                  <Button
                    onClick={openCreateCategoriaModal}
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Nova Categoria
                  </Button>
                )}
              </div>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)]">Categorias</CardTitle>
              <CardDescription>
                {categorias.length} categoria(s) cadastrada(s) — arraste as setas para definir a ordem de exibição na loja
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">Carregando...</div>
                </div>
              ) : categorias.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhuma categoria cadastrada
                </div>
              ) : (
                <div className="space-y-2">
                  {categorias.map((categoria, index) => (
                    <div
                      key={categoria.id}
                      className="flex items-center gap-3 p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex flex-col gap-1 mr-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveCategoria(index, "up")}
                          disabled={index === 0}
                          className="h-6 w-6 p-0"
                        >
                          <ArrowUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveCategoria(index, "down")}
                          disabled={index === categorias.length - 1}
                          className="h-6 w-6 p-0"
                        >
                          <ArrowDown className="h-3 w-3" />
                        </Button>
                      </div>

                      <FolderOpen className="w-5 h-5 text-[oklch(0.45_0.15_142)] shrink-0" />

                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground">{categoria.nome}</h3>
                        <p className="text-xs text-muted-foreground">Ordem: {categoria.ordem}</p>
                      </div>

                      <div className="flex items-center gap-2">
                        {hasPermission(PERMISSOES.EDITAR_EMBLEMA) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditCategoriaModal(categoria)}
                            className="text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 hover:bg-[oklch(0.45_0.15_142)]/10"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                        )}
                        {hasPermission(PERMISSOES.EXCLUIR_EMBLEMA) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openExcluirCategoriaModal(categoria.id, categoria.nome)}
                            className="text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Excluir
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal: Criar/Editar Categoria */}
      {isModalCategoriaOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-[oklch(0.45_0.15_142)]" />
                {editingCategoria ? "Editar Categoria" : "Nova Categoria"}
              </CardTitle>
              <CardDescription>
                {editingCategoria ? "Atualize o nome da categoria" : "Crie uma nova categoria para organizar os emblemas"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                title="Nome da Categoria"
                value={formNomeCategoria}
                onChange={(e) => setFormNomeCategoria(e.target.value)}
                placeholder="Ex: Eventos, Conquistas..."
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveCategoria}
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  {editingCategoria ? "Atualizar" : "Criar"}
                </Button>
                <Button onClick={closeCategoriaModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal: Excluir Categoria */}
      {isExcluirCategoriaModalOpen && categoriaToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Excluir Categoria
              </CardTitle>
              <CardDescription>Esta ação não pode ser desfeita</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-950/30 dark:border-red-800">
                <p className="text-sm text-foreground">
                  Tem certeza que deseja excluir a categoria{" "}
                  <span className="font-semibold text-green-700 dark:text-green-400">
                    {categoriaToDelete.nome}
                  </span>
                  ?
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Os emblemas desta categoria não serão excluídos, apenas perderão o vínculo com a categoria.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleDeleteCategoria}
                  disabled={loading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? "Excluindo..." : "Sim, Excluir"}
                </Button>
                <Button onClick={closeExcluirCategoriaModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
